/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigationContext } from '@restworld/utils-common';
import { ReactNode } from 'react';
import { cn } from '@restworld/utils-common';

export interface LinkBaseProps {
  children: ReactNode;
  link?: string;
  hasUnderline?: boolean;
  $hasUnderline?: boolean;
  twStyle?: string | string[];
  newTab?: boolean;
  onLinkClick?: (event?: React.SyntheticEvent<HTMLAnchorElement, Event>) => void;
  className?: string;
}
export const LinkBase = ({
  children,
  link = '#!',
  hasUnderline,
  twStyle,
  newTab = true,
  onLinkClick,
  className,
  ...rest
}: LinkBaseProps) => {
  const { linkComponent: LinkComponent, router }: { linkComponent: React.ElementType; router: any } =
    useNavigationContext();

  // A `newTab` link opens a new document, so the router can never handle the navigation and a
  // prefetched payload can never be used. Passing `prefetch={false}` does not stop it: in
  // `next/dist/client/link.js` only the viewport effect is gated on `prefetchEnabled`, while the
  // `onMouseEnter` and `onTouchStart` handlers call `prefetch()` unconditionally and with
  // `bypassPrefetchedCheck: true`, so they re-fire on every hover or tap. Leaving the router out
  // entirely is the only way to stop the requests.
  // Measured on production 17/09/2026: job and venue cards accounted for ~58k of the ~130k failed
  // `/_next/data` requests a week, none of which could have been used even had they succeeded.
  const useRouterLink = !!link && !!LinkComponent && !newTab;
  const Tag = (useRouterLink ? LinkComponent : 'a') as any;

  // `<Link>` prefixes the active locale; a plain anchor has to do it itself, or an English visitor
  // clicking a card would be sent to the Italian page. Only a root-relative path is ours to
  // rewrite: `//host` is protocol-relative and points off-site, and a path that already carries
  // the locale must not get a second one.
  const locale = router?.locale;
  const needsLocalePrefix =
    !useRouterLink &&
    !!locale &&
    locale !== router?.defaultLocale &&
    link.startsWith('/') &&
    !link.startsWith('//') &&
    link !== `/${locale}` &&
    !link.startsWith(`/${locale}/`);
  const href = needsLocalePrefix ? `/${locale}${link}` : link;

  return (
    <Tag
      className={cn(
        'text-primary-800 cursor-pointer',
        hasUnderline && 'hover:underline hover:underline-offset-4',
        ...(twStyle && Array.isArray(twStyle) ? twStyle : [twStyle]),
        className
      )}
      onClick={(e: React.SyntheticEvent<HTMLAnchorElement, Event>) => {
        if (onLinkClick) onLinkClick(e);
        e.stopPropagation();
      }}
      href={href}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};
export default LinkBase;
